import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { PageHeader } from '../../components/layout/PageHeader';
import { AssetTable } from '../../components/assets/AssetTable';
import { useAssetIndexStore } from '../../stores/asset-index';
import { usePresenceStore } from '../../stores/presence';
import { Search, X } from 'lucide-react';

export function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchInput, setSearchInput] = useState(searchParams.get('q') || '');

  const searchQuery = searchParams.get('q') || '';

  const storeSetFilter = useAssetIndexStore(s => s.setFilter);
  useEffect(() => { storeSetFilter({ search: searchQuery || undefined }); }, [searchQuery, storeSetFilter]);
  useAssetIndexStore.live();
  const assets = useAssetIndexStore(s => s.assets);
  const total = useAssetIndexStore(s => s.total);
  const isLoading = useAssetIndexStore(s => s._loading);
  const data = isLoading ? undefined : { assets, total };

  usePresenceStore.live({ entity: "asset" });
  const presence = usePresenceStore(s => s.presence);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchInput.trim()) {
      setSearchParams({ q: searchInput.trim() });
    }
  };

  const clearSearch = () => {
    setSearchInput('');
    setSearchParams({});
  };

  return (
    <>
      <PageHeader
        title="Search Assets"
        description="Search across all asset fields"
        breadcrumbs={[
          { label: 'Assets', href: '/assets' },
          { label: 'Search' },
        ]}
      />

      <main className="flex-1 overflow-y-auto bg-muted/40">
        <div className="container mx-auto p-6 lg:p-8">
          <div className="flex flex-col gap-6">
            <form onSubmit={handleSearch} className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Search by nickname, description, location..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Button type="submit">Search</Button>
              {searchQuery && (
                <Button type="button" variant="outline" onClick={clearSearch}>
                  <X className="h-4 w-4" />
                </Button>
              )}
            </form>

            {searchQuery && (
              <div className="flex flex-col gap-4">
                <div className="text-sm text-muted-foreground">
                  {isLoading ? (
                    'Searching...'
                  ) : (
                    <>
                      Found <span className="font-medium">{data?.total || 0}</span> result
                      {data?.total !== 1 ? 's' : ''} for{' '}
                      <span className="font-medium">"{searchQuery}"</span>
                    </>
                  )}
                </div>

                <AssetTable
                  assets={data?.assets || []}
                  presence={presence}
                  isLoading={isLoading}
                />
              </div>
            )}

            {!searchQuery && (
              <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
                Enter a search query to find assets
              </div>
            )}
          </div>
        </div>
      </main>
    </>
  );
}
